import type { LWApiPublicBillingPaymentInstrumentDetailsRaw } from '@liquidweb-dev/club-sauce/public/billing/payment/instrument/index.raw';

/**
 * What a method *is*, for display purposes.
 *
 * Not the same axis as `type` or `wallet` alone. `type` gives the family but cannot tell
 * a plain card from Apple Pay; `wallet` refines a Stripe card but reads `card` on a Stripe
 * bank account, because `_walletType` falls back to that literal when a PaymentMethod has
 * no card block. Reading either on its own renders a bank account as a Visa.
 */
export type PaymentMethodKindT =
	| 'applePay'
	| 'bank'
	| 'card'
	| 'check'
	| 'googlePay'
	| 'link'
	| 'payPal'
	| 'unknown';

export interface PaymentMethodDescriptorI {
	/**
	 * The card network, normalised to lowercase with underscores, e.g. `american_express`.
	 * Absent when the method has no brand.
	 */
	brand?: string;
	kind: PaymentMethodKindT;
	/** The row label, e.g. `•••• •••• •••• 1117`, `Apple Pay`, `CHECKING`. */
	label: string;
	/** The last four digits, bare, e.g. `1117`. Absent when the method has none. */
	last4?: string;
	/**
	 * The **emphasised** part of the removal-dialog descriptor, e.g. `card ending in 1117`
	 * or `PayPal`.
	 *
	 * Excludes the article, which the design renders unemphasised — the card variant reads
	 * "remove the **card ending in 1234**", not "remove **the card ending in 1234**".
	 */
	phrase: string;
	/**
	 * The article preceding {@link phrase}, rendered unemphasised, or absent for a proper
	 * noun. Varies by method rather than being fixed in the sentence: the PayPal variant
	 * reads "remove **PayPal**" with no article at all.
	 */
	phraseArticle?: string;
	/**
	 * A second, quieter label rendered after {@link label} — the bank name on a bank row.
	 *
	 * Sourced from the instrument's `description`, and **bank rows only**. `description` is
	 * free text the customer sets to whatever they like; it is not derived from the
	 * processor and there is no bank-name field in the payload. It is also optional
	 * (`LW/Account/Stripe.pm:380`), so a bank row frequently has none and simply renders
	 * without it.
	 */
	secondaryLabel?: string;
}

/** The masked portion of an account number, e.g. `•••• •••• •••• 4242`. */
export const MASK = '•••• •••• ••••';

/**
 * Brands arrive in more than one vocabulary. Stripe reports `amex`; Authorize.Net's live
 * path reports the lowercase enum, while its migration path reports spelled-out mixed
 * case (`American Express`). Normalising to one spelling means downstream icon selection
 * has a single vocabulary to match on.
 */
const BRAND_ALIASES: Record<string, string> = {
	amex: 'american_express',
};

const normaliseBrand = (brand?: string): string | undefined => {
	if (!brand) {
		return undefined;
	}

	const normalised = brand.trim().toLowerCase().replace(/\s+/g, '_');

	return BRAND_ALIASES[normalised] ?? normalised;
};

/**
 * Pulls the last four digits off a value that may be masked.
 *
 * Authorize.Net's `token` is an obfuscated number such as `XXXX1117`, so the digits are
 * the trailing run rather than the whole string. Stripe's `card_last4` is already bare
 * and passes through unchanged.
 */
const stripMask = (value?: string): string | undefined =>
	value?.match(/(\d{1,4})$/)?.[1];

/** Resolves the display kind, consulting `type`, then `processor`, then `wallet`. */
const getKind = (
	instrument: LWApiPublicBillingPaymentInstrumentDetailsRaw,
): PaymentMethodKindT => {
	// Checked first, and deliberately: a Stripe bank account carries `wallet: 'card'`.
	if (instrument.type === 'ach') {
		return 'bank';
	}

	if (instrument.processor === 'paypal') {
		return 'payPal';
	}

	if (instrument.type === 'check') {
		return 'check';
	}

	if (instrument.processor === 'stripe') {
		switch (instrument.metadata.wallet) {
			case 'apple_pay':
				return 'applePay';
			case 'google_pay':
				return 'googlePay';
			case 'link':
				return 'link';
			case 'card':
				return 'card';
			// `_walletType` forwards Stripe's value verbatim, so `samsung_pay` and others
			// are reachable. An unrecognised wallet is still a card underneath.
			default:
				return 'card';
		}
	}

	return instrument.type === 'credit_card' ? 'card' : 'unknown';
};

/** The bank/account label a legacy ACH row carries, e.g. `CHECKING`. */
const getAccountDesc = (
	instrument: LWApiPublicBillingPaymentInstrumentDetailsRaw,
): string | undefined =>
	instrument.processor === 'authorize_net'
		? instrument.metadata.ach_account_desc
		: undefined;

const getRawLast4 = (
	instrument: LWApiPublicBillingPaymentInstrumentDetailsRaw,
): string | undefined => {
	// Preferred over `metadata` where present. Card instruments only, so the
	// per-processor reads below remain the fallback for everything else.
	if (instrument.cardDetails?.last4) {
		return instrument.cardDetails.last4;
	}

	if (instrument.processor === 'stripe') {
		return instrument.metadata.card_last4;
	}

	if (instrument.processor === 'authorize_net') {
		// `token`, never `issuer_number` — the latter is the IIN, the *leading* digits.
		return instrument.metadata.token;
	}

	return undefined;
};

export const getLast4 = (
	instrument: LWApiPublicBillingPaymentInstrumentDetailsRaw,
) => stripMask(getRawLast4(instrument));

const getRawBrand = (
	instrument: LWApiPublicBillingPaymentInstrumentDetailsRaw,
): string | undefined => {
	// See `getRawLast4` — same `cardDetails`-first, `metadata`-fallback shape.
	if (instrument.cardDetails?.issuer) {
		return instrument.cardDetails.issuer;
	}

	if (instrument.processor === 'stripe') {
		return instrument.metadata.card_brand;
	}

	if (instrument.processor === 'authorize_net') {
		// `echeck` is a bank marker rather than a card network, so it is not a brand.
		const { issuer } = instrument.metadata;

		return issuer === 'echeck' ? undefined : issuer;
	}

	return undefined;
};

export const getBrand = (
	instrument: LWApiPublicBillingPaymentInstrumentDetailsRaw,
) => normaliseBrand(getRawBrand(instrument));

const WALLET_NAMES: Record<'applePay' | 'googlePay' | 'link', string> = {
	applePay: 'Apple Pay',
	googlePay: 'Google Pay',
	link: 'Link',
};

/**
 * Describes a payment method for display: its kind, its row label, and the phrase the
 * removal dialog drops into its sentence.
 *
 * Row label and dialog phrase are separate strings for the same method — the row renders
 * `•••• •••• •••• 1117` while the dialog says `the card ending in 1117` — so they are produced
 * together here rather than derived independently and left to drift.
 *
 * Phrases for bank, wallet, and check methods are inferred from the two variants the
 * design draws (card and PayPal) and are not signed off — see task 10.3.
 */
const describePaymentMethod = (
	instrument: LWApiPublicBillingPaymentInstrumentDetailsRaw,
): PaymentMethodDescriptorI => {
	const kind = getKind(instrument);
	const last4 = getLast4(instrument);
	const brand = getBrand(instrument);
	const base = { brand, kind, last4 };

	switch (kind) {
		case 'applePay':
		case 'googlePay':
		case 'link': {
			// The design labels a wallet by name rather than by digits, even where the
			// underlying card has them.
			const name = WALLET_NAMES[kind];

			return { ...base, label: name, phrase: name };
		}

		case 'payPal':
			return { ...base, label: 'PayPal', phrase: 'PayPal' };

		case 'check':
			return {
				...base,
				label: 'Check',
				phrase: 'payment method',
				phraseArticle: 'this',
			};

		case 'bank': {
			/*
			 * **Deliberately diverges from the design.** Figma `3875-17056` labels a bank row
			 * with its account description alone — `CHECKING` — but a masked number
			 * identifies the account, where the description only names its kind. So the
			 * masked number is the label wherever one exists, matching how a card row reads.
			 *
			 * The description is a fallback rather than a prefix: it appears only when there
			 * are no digits to show. A Stripe bank account has the opposite gap — digits but
			 * no `ach_account_desc` — so both fallbacks are reachable.
			 */
			return {
				...base,
				label:
					(last4 && `${MASK} ${last4}`) ||
					getAccountDesc(instrument) ||
					'Bank account',
				phrase: last4 ? `bank account ending in ${last4}` : 'bank account',
				phraseArticle: last4 ? 'the' : 'this',
				// The bank name, per design. Bank rows only — a card's `description` is a
				// personal label ("Personal Visa") and is not shown.
				secondaryLabel: instrument.description,
			};
		}

		case 'card':
			return {
				...base,
				label: last4 ? `${MASK} ${last4}` : 'Card',
				phrase: last4 ? `card ending in ${last4}` : 'card',
				phraseArticle: last4 ? 'the' : 'this',
			};

		default:
			return {
				...base,
				label: 'Payment method',
				phrase: 'payment method',
				phraseArticle: 'this',
			};
	}
};

export default describePaymentMethod;
